import React, { useState, useEffect, useCallback } from 'react';
import {
  ElementEqualIcon,
  CultureCustomsCard,
  SearchBar,
  DayCard,
  LoadMoreButton,
  SeasonsDropdown,
} from '../components';
import multiMonthPlugin from '@fullcalendar/multimonth';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "src/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select";
import { fetchCultureAndCustomsListData, toggleFavorite, fetchLiturgicalEvents } from '../utilities/api';
import { Skeleton } from '../components/ui/skeleton';
import useLiturgicalEvents from '../hooks/useLiturgicalEvents';
import { mapCultureCustomsListData } from '../utilities';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
// Your existing static events
const staticEvents = [
  { title: 'Day', date: '2024-12-25T10:30:00', classNames: ['bda-abstinence-event'] },
  // ... rest of your static events
];
const views = {
  multiMonthFourMonth: {
    type: 'multiMonth',
    duration: { months: 2 }
  }
}
function PageCultureAndCustoms() {
  const [cultureData, setCultureData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchItems, setSearchItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [debouncedSeason, setDebouncedSeason] = useState("all");
  const itemsPerPage = 9;
  const [totalItems, setTotalItems] = useState(0);
  const [offset, setOffset] = useState(0);
  const [itemsPerBatch, setItemsPerBatch] = useState(9);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [apiEvents, setApiEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('All');
  const [isAccordionVisible, setIsAccordionVisible] = useState(false);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [LiturgicalApiEvents, setLiturgicalApiEvents] = useState([])
  const [isDayCardLoading, setIsDayCardLoading] = useState(true)
  const today = new Date();
  const { isDataLoading, eventData } = useLiturgicalEvents(
    today.getFullYear(),
    today.getMonth() + 1,
    today.getDate()
  );
  // Ensure skeleton stays until API data is loaded
  useEffect(() => {
    if (!isDataLoading && eventData) {
      setIsDayCardLoading(false); // Hide skeleton only when API data is fully loaded
    }
  }, [isDataLoading, eventData]);
  // Extract relevant event details
  const firstEventKey = eventData ? Object.keys(eventData)[0] : null;
  const firstEvent = firstEventKey ? eventData[firstEventKey] : null;
  // Ensure no flickering or static data issues
  const dayData = isDataLoading
    ? {} //Show skeleton while loading (NO STATIC DATA)
    : {
      date: firstEvent?.date
        ? new Date(firstEvent.date * 1000).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
        : "",
      title: firstEvent?.name || "",
      tag: firstEvent?.liturgicalSeasonLcl || "",
      label: firstEvent ? "Today" : "",
      category: "Easter",
      description: firstEvent?.eventData?.description || '',
    };
  const filteredEvents = useCallback(() => {
    const allEvents = [...staticEvents, ...apiEvents];
    if (activeTab === "All") return allEvents;
    return allEvents.filter(event => {
      // Safely handle events with potential undefined properties
      const title = (event.title || '').toLowerCase();
      const classNames = event.classNames || [];
      // Check if any class matches the active tab
      const tabLower = activeTab.toLowerCase();
      return classNames.some(cn => cn.includes(tabLower)) ||
        (tabLower === 'feast' && (
          title.includes('virgin') ||
          title.includes('feast') ||
          title.includes('sunday') ||
          title.includes('easter') ||
          title.includes('pentecost')
        )) ||
        (tabLower === 'fast' && (
          title.includes('martyr') ||
          title.includes('fast') ||
          title.includes('good friday')
        )) ||
        (tabLower === 'abstinence' && (
          title.includes('bishop') ||
          title.includes('abstinence') ||
          title.includes('friday') ||
          title.includes('lent')
        ));
    });
  }, [activeTab, apiEvents, staticEvents]);
  // Debounce the search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);
  // Fetch Data with Correct Category Filter
  useEffect(() => {
    console.log("Fetching Data - Selected Category:", selectedCategory, "Search Query:", searchQuery);
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchCultureAndCustomsListData(0, itemsPerBatch, debouncedSearchQuery, selectedCategory, debouncedSeason);
        const responseData = response.data || [];
        const includedData = response.included || [];
        const totalRecords = response.meta?.pagination?.total || 0;
        const batchLimit = response.meta?.pagination?.limit || 9;
        const transformedData = mapCultureCustomsListData(responseData, includedData);
        setCultureData(transformedData);
        setTotalItems(totalRecords);
        setItemsPerBatch(batchLimit);
        setHasMore(batchLimit < totalRecords);
        setOffset(batchLimit);
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };
    if (debouncedSearchQuery.length === 0 || debouncedSearchQuery.length > 2) {
      loadData();
    }
    // loadData();
  }, [debouncedSearchQuery, selectedCategory, debouncedSeason]);
  useEffect(() => {
    console.log(selectedSeason, '---------selectedSeason');
    const handler = setTimeout(() => {
      setDebouncedSeason(selectedSeason);
    }, 500);
    return () => clearTimeout(handler);
  }, [selectedSeason]);
  useEffect(() => {
    const loadLiturgicalApiEvents = async () => {
      try {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        const response = await fetchLiturgicalEvents(year, month);
        const eventList = Object.values(response || {}).map(event => ({
          title: event.name,
          date: new Date(event.date * 1000).toISOString().split('T')[0],
          classNames: determineCategoryClass(event.name)
        }));
        setLiturgicalApiEvents(eventList);
      } catch (error) {
        console.error("Failed to fetch liturgical events:", error);
      }
    };
    loadLiturgicalApiEvents();
  }, []);
  const renderEventContent = (eventInfo) => {
    // Ensure we have class names
    const classNames = eventInfo.event.classNames
      ? eventInfo.event.classNames
      : ['bda-normal-event'];
    const icon = getEventIcon(classNames);
    return (
      <div className="flex items-center justify-center bda-calendar-event">
        <div className="flex flex-col items-center justify-center">
          {/* <span className="mb-1 text-xs text-center text-gold-500">
          {eventInfo.event.title}
          </span> */}
          <div className="w-5 h-5 mt-4">
            {icon}
          </div>
        </div>
      </div>
    );
  };
  const determineCategoryClass = (title) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('martyr')) return ['bda-fast-event'];
    if (lowerTitle.includes('apostle')) return ['bda-feast-event'];
    if (lowerTitle.includes('bishop')) return ['bda-abstinence-event'];
    if (lowerTitle.includes('virgin')) return ['bda-feast-event'];
    if (lowerTitle.includes('confessor')) return ['bda-feast-event'];
    if (lowerTitle.includes('pope')) return ['bda-feast-event'];
    if (lowerTitle.includes('presentation')) return ['bda-feast-event'];
    return ['bda-normal-event'];
  };
  // Update getEventIcon to be more robust
  const getEventIcon = (classNames) => {
    const classString = (Array.isArray(classNames) ? classNames : [classNames])
      .join(' ')
      .toLowerCase();
    if (classString.includes('fast')) {
      return <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#610F7F]"></span>;
    } else if (classString.includes('feast')) {
      return <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#1A856B]"></span>;
    } else if (classString.includes('abstinence')) {
      return <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#E84A5A]"></span>;
    } else {
      return <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#876B2E]"></span>;
    }
  };
  const handleEventClick = (info) => {
    console.log(`Event Title: ${info.event.title}`);
    setIsAccordionVisible(prevState => !prevState);
  };
  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    try {
      setLoadingMore(true);
      const response = await fetchCultureAndCustomsListData(offset, itemsPerPage, searchQuery, selectedCategory, selectedSeason)
      const responseData = response.data || [];
      const includedData = response.included || [];
      const newOffset = offset + itemsPerBatch;
      setHasMore(newOffset < totalItems);
      setOffset(newOffset);
      if (responseData.length > 0) {
        const transformedData = mapCultureCustomsListData(responseData, includedData);
        setCultureData((prev) => [...prev, ...transformedData]);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading more data:", error);
      setError("Failed to load more items.");
    } finally {
      setLoadingMore(false);
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const values = Object.fromEntries(formData.entries());
    setSelectedCategory(values.Categories || 'all');
    setSelectedSeason(values.Seasons || 'all');
  };
  const handleToggleFavorite = async (id) => {
    try {
      const currentFavorite = cultureData.find(item => item.id === id)?.favorite;
      // Optimistically update UI
      setCultureData((prevData) =>
        prevData.map((item) =>
          item.id === id ? { ...item, favorite: !item.favorite } : item
        )
      );
      await toggleFavorite('culture-customs', id, false, currentFavorite); // API Call in the background
    } catch (error) {
      console.error("Failed to update favorite:", error);
      // If API call fails, revert UI
      setCultureData((prevData) =>
        prevData.map((item) =>
          item.id === id ? { ...item, favorite: !item.favorite } : item
        )
      );
    }
  };
  const handleSearchQuery = (value) => {
    setSearchQuery(value);  // Ensure state updates
    setDebouncedSearchQuery(value);
  };
  // Handle Category Change
  const categorySlugMap = {
    "sacred-art": "ps-sacred-art",
    "sacred-music": "ps-sacred-music",
    "prose-poetry": "ps-prose-poetry",
    "family-activities": "ps-family-activity",
    "recipes": "ps-recipe",
    "history": "ps-history",
    "national-customs": "ps-national-customs",
  };
  //Handle Category Selection
  const handleCategoryChange = (value) => {
    // If "all" is selected, store it directly
    if (value === "all") {
      setSelectedCategory("all");
    } else {
      setSelectedCategory(value); // Already ps-* slug
    }
    setCultureData([]);
    setOffset(0);
  };
  useEffect(() => {
    if (cultureData.length > 0) {
      const items = cultureData.map(item => ({
        id: item.id,
        name: item.title || item.name, // Assuming your items have a title or name field
      }));
      setSearchItems(items);
    }
  }, [cultureData]);
  return (
    <>
      <div className="bg-white-50 max-w-full w-full lg:w-[60%] xl:w-[67.33%]">
        <div className="px-4 py-8 pl-6 xl:px-6">
          <section>
            <div className='mb-4 md:mb-6'>
              <div className='flex flex-wrap items-center justify-between gap-6 md:gap-3'>
                <div className="flex items-center gap-2">
                  <span className='w-6 h-6 mt-px text-gold-200'>
                    <ElementEqualIcon className='w-full h-full' />
                  </span>
                  <h1 className='text-2xl font-bold leading-7 text-gray-900 md:text-xl xl:text-2xl font-cmr'>
                    Culture & Customs
                  </h1>
                </div>
                <SearchBar
                  inputName="s"
                  inputPlaceholder="Search by name"
                  items={searchItems} // Pass filtered search items
                  onSearch={handleSearchQuery}
                />
              </div>
              <div className='mt-8'>
                <h5 className="font-bold leading-5 text-gray-900 font-base">All Items</h5>
                <form onSubmit={handleSubmit}>
                  <div className='flex flex-wrap gap-3 mt-4'>
                    {
                      loading ? (
                        <Skeleton className="h-[34px] w-[166px] mb-2" />
                      ) : (
                        <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                          <SelectTrigger name="Categories">
                            <SelectValue placeholder="All" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            {Object.keys(categorySlugMap).map((key) => (
                              <SelectItem key={key} value={categorySlugMap[key]}>{key.replace('-', ' ')}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )
                    }

                    <div className=''>
                      {
                        loading ? (
                          <Skeleton className="h-[34px] w-[166px] mb-2" />
                        
                        ):(

                          <SeasonsDropdown value={selectedSeason} onChange={(value) => setSelectedSeason(value)} />
                        )
                      }
                    </div>
                  </div>
                </form>
              </div>
            </div>
            {loading ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3">
                {Array.from({ length: itemsPerPage }).map((_, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-md ">
                    <Skeleton className="h-[219px] w-full rounded-lg" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center text-red-500">{error}</div>
            ) : cultureData.length === 0 ? (
              // No Results Found Message
              <div className="mt-6 text-center text-gray-500">
                No results found. Try a different search or category.
              </div>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3">
                  {cultureData.map((item) => (
                    <CultureCustomsCard key={item.slug} {...item} imageUrl={item.imageUrl} onToggleFavorite={handleToggleFavorite} />
                  ))}
                </div>
                {hasMore && cultureData.length > 0 && (
                  <div className="flex justify-center mt-6">
                    <LoadMoreButton
                      label="Load More"
                      onClick={handleLoadMore}
                      isLoading={loadingMore}
                      disabled={!hasMore || loadingMore}
                    />
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>
      <div className="lg:ml-0 md:h-auto bg-gold-50 max-w-full w-full lg:w-[40%] xl:w-[32.67%]">
        <div className="top-0 flex flex-col px-4 py-8 xl:px-6 md:sticky">
          <div class="p-4 rounded-lg bg-white-50 mb-8 bda-full-calendar mini culture-calendar hidden md:block">
            <FullCalendar
              plugins={[dayGridPlugin]}
              initialView='dayGridMonth'
              weekends={true}
              events={loading ? [] : filteredEvents()}
              eventContent={renderEventContent}
              eventDisplay='block' // Ensure events are displayed
              default={false}
              dayMaxEvents={true}
              views={views}
              eventClick={handleEventClick}
              headerToolbar={{
                left: 'title',
              }}
              height="auto"
              contentHeight="auto"
            />
          </div>
          <DayCard data={dayData} isLoading={isDayCardLoading} />
        </div>
      </div>
      <div className="block md:hidden">
        <Tabs defaultValue="All" className="flex flex-col items-center justify-center mt-4">
          <TabsList className="p-1 bg-gold-50 week-calender-tab-header">
            {["All", "Feast", "Abstinence", "Fast"].map(tab => (
              <TabsTrigger
                key={tab}
                value={tab}
                onClick={() => setActiveTab(tab)}
                className={`p-0 text-sm	font-medium tab-name-${tab.toLowerCase()} ${activeTab === tab ? "active" : ""}`}
              >
                {tab}
              </TabsTrigger>
            ))}
          </TabsList>
          {["All", "Feast", "Abstinence", "Fast"].map(tab => (
            <TabsContent key={tab} value={tab} className="overflow-hidden">
              <div className="bda-full-calendar bda-compact-calendar">
                <FullCalendar
                  plugins={[multiMonthPlugin]}
                  initialView="multiMonthFourMonth"
                  weekends={true}
                  headerToolbar={false}
                  eventDisplay='block'
                  events={loading ? [] : filteredEvents()}
                  eventContent={renderEventContent}
                  views={{
                    multiMonthFourMonth: {
                      type: "multiMonth",
                      duration: { months: 2 }
                    }
                  }}
                  eventClick={handleEventClick}
                  height="auto"
                  contentHeight="auto"
                />
                <div className="pl-4 py-4 bg-gold-50 fixed bottom-[88px] left-0 right-0 w-full z-5 md:hidden block">
                  <p className="text-xs font-normal leading-4 text-gray-500">Season:<span className="px-2 py-1 mt-4 ml-1 font-medium bg-white-50 text-gold-500 rounded-27">Easter</span></p>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </>
  );
}
export default PageCultureAndCustoms;
